import React, { createContext, useContext, ReactNode } from 'react';
import Seo from '@/components/Seo';

type RenderContext = {
  url: string;
  meta: {
    title?: string;
    description?: string;
    ogImage?: string;
    canonical?: string;
    locale?: string;
    siteUrl: string;
    indexable?: boolean;
    siteName?: string;
    jsonLd?: Record<string, unknown>;
  };
  tenant: { 
    name: string;
    primaryColor?: string;
    secondaryColor?: string;
  };
};

interface SeoContextType {
  isSSR: boolean;
  ssrContext?: RenderContext;
}

const SeoContext = createContext<SeoContextType>({
  isSSR: false
});

export const useSeo = () => useContext(SeoContext);

interface SeoProviderProps {
  children: ReactNode;
  isSSR: boolean;
  ssrContext?: RenderContext;
}

export const SeoProvider = ({ children, isSSR, ssrContext }: SeoProviderProps) => {
  console.log('[SeoProvider] Rendering with:', { isSSR, hasSSRContext: !!ssrContext });

  // Always use server context when available (both SSR and client hydration)
  // Only fall back to basic defaults when no server context exists
  const seoData = ssrContext ? {
    // Use server-provided context (available during SSR and client hydration)
    title: ssrContext.meta.title,
    description: ssrContext.meta.description,
    siteUrl: ssrContext.meta.siteUrl,
    canonicalPath: ssrContext.meta.canonical,
    image: ssrContext.meta.ogImage,
    locale: ssrContext.meta.locale,
    siteName: ssrContext.meta.siteName,
    indexable: ssrContext.meta.indexable,
    jsonLd: ssrContext.meta.jsonLd,
  } : {
    // Basic fallback matching generateFallbackSeo (should rarely happen in normal operation)
    title: 'Community Platform',
    description: 'Discover and connect with trusted local service providers in your community.',
    siteUrl: window.location.origin,
    canonicalPath: window.location.pathname,
    siteName: 'Community Platform',
    locale: 'en_GB',
    indexable: true,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Community Platform",
      "description": "Discover and connect with trusted local service providers in your community.",
      "url": window.location.origin
    }
  };

  console.log('[SeoProvider] Final SEO data:', seoData);

  return (
    <SeoContext.Provider value={{ isSSR, ssrContext }}>
      {/* Render SEO component immediately, regardless of other providers */}
      <Seo
        title={seoData.title}
        description={seoData.description}
        siteUrl={seoData.siteUrl}
        canonicalPath={seoData.canonicalPath}
        image={seoData.image}
        locale={seoData.locale}
        siteName={seoData.siteName}
        indexable={seoData.indexable}
        jsonLd={seoData.jsonLd}
      />
      
      {/* Render children (the rest of the app) */}
      {children}
    </SeoContext.Provider>
  );
};